import { useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultFormDatePickerInput } from "../../../../../../common/components/default_form_date_picker_input";
import { AvaliacaoOrdemCompraColetaForm, AvaliacaoOrdemCompraColetaSchema } from "../../../schemas/AvaliacaoFornecedor_cadastro/avaliacao_ordem_compra_coleta";
import { useCallback, useEffect, useMemo } from "react";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { GridColDef } from "@mui/x-data-grid-pro";
import { ProdutoServicoParaEntrega } from "../../../../domain/models/produto_servicos_para_entrega";
import { CustomNumberMaskedInput } from "./custom_input_number_mask";
import { CustomInput } from "./custom_input";
import { DefaultFormDownloadInput } from "../../../../../../common/components/default_form_download_input";
import { DefaultFormCheckBox } from "../../../../../../common/components/default_form_checkbox";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import ItemStatus from "../../../../../../common/components/item_status";

/**
 * Column definition
 */

const GenerateColumnDefinition = (
  onQuantidadeRecebidaChange: (idProdutoServico: number, quantidadeRecebida: number | null) => void,
  onObservacaoChange: (idProdutoServico: number, observacao: string | null) => void,
): GridColDef[] => {
  const computeTipoTitle = (status: number) => {
    switch (status) {
      case 1:
        return "Produto";
      case 0:
        return "Serviço";
      default:
        return "?";
    }
  };

  const computeTipoColor = (status: number) => {
    switch (status) {
      case 1:
        return "green";
      case 0:
        return "yellow";
      default:
        return "blue";
    }
  };

  const computeTipoDescription = (status: number) => {
    switch (status) {
      case 1:
        return "Produto";
      case 0:
        return "Serviço";
      default:
        return "?";
    }
  };

  return [
    {
      field: "tipo",
      filterable: false,
      headerName: "Tipo",
      minWidth: 80,
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full h-full flex justify-center items-center">
          <ItemStatus
            size="autowidth"
            textSize="small"
            title={computeTipoTitle(params.value === 'P' ? 1 : 0)}
            color={computeTipoColor(params.value === 'P' ? 1 : 0)}
            description={computeTipoDescription(params.value === 'P' ? 1 : 0)} />
        </div>
      )
    },
    {
      field: "descricao",
      filterable: false,
      headerName: "Produto/Serviço",
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "quantidade",
      filterable: false,
      headerName: "Quantidade",
      width: 100,
      minWidth: 100,
      sortable: false,
      renderCell: ({ value }) => (
        <>
          {
            value !== null && value !== undefined
              ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
              : ''
          }
        </>
      )
    },
    {
      field: "quantidadeRestante",
      filterable: false,
      headerName: "Quantidade Restante",
      width: 100,
      minWidth: 100,
      sortable: false,
      renderCell: ({ value }) => (
        <>
          {
            value !== null && value !== undefined
              ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
              : ''
          }
        </>
      )
    },
    {
      field: "quantidadeRecebida",
      filterable: false,
      headerName: "Quantidade Recebida",
      width: 150,
      minWidth: 120,
      sortable: false,
      valueGetter: (_, row) => [row['idProdutoServico'], row['quantidadeRecebida']],
      renderCell: ({ value: [idProdutoServico, quantidadeRecebida] }) => {
        return (
          <CustomNumberMaskedInput
            value={quantidadeRecebida}
            onValueChange={e => onQuantidadeRecebidaChange(idProdutoServico, e)}
          />
        );
      }
    },
    {
      field: "observacao",
      filterable: false,
      headerName: "Observação",
      width: 250,
      minWidth: 150,
      sortable: false,
      valueGetter: (_, row) => [row['idProdutoServico'], row['observacao']],
      renderCell: ({ value: [idProdutoServico, observacao] }) => (
        <CustomInput
          value={observacao}
          onValueChange={e => onObservacaoChange(idProdutoServico, e)}
        />
      )
    },
  ];
}

/**
 * AvaliacaoOrdemCompraColetaModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  produtosServicosParaEntrega: ProdutoServicoParaEntrega[];
  onClose: () => void;
  onSubmit: (form: AvaliacaoOrdemCompraColetaForm) => void;
};

export const AvaliacaoOrdemCompraColetaModal = ({
  open,
  saving,
  loading,
  produtosServicosParaEntrega,
  onClose,
  onSubmit,
}: Props) => {

  const hookForm = useForm<AvaliacaoOrdemCompraColetaForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(AvaliacaoOrdemCompraColetaSchema),
  });

  const [produtosServicos] = hookForm.watch(['produtosServicos']);
  const handleQuantidadeRecebidaChange = useCallback((idProdutoServico: number, quantidadeRecebida: number | null) => {
    const currentProdutosServicos = [...hookForm.getValues('produtosServicos')];
    const changedPSIndex = currentProdutosServicos.findIndex(x => x.idProdutoServico === idProdutoServico);
    if (changedPSIndex >= 0) {
      currentProdutosServicos[changedPSIndex].quantidadeRecebida = quantidadeRecebida;
      hookForm.setValue('produtosServicos', [...currentProdutosServicos]);
    }
  }, [
    hookForm,
  ]);

  const handleObservacaoChange = useCallback((idProdutoServico: number, observacao: string | null) => {
    const currentProdutosServicos = [...hookForm.getValues('produtosServicos')];
    const changedPSIndex = currentProdutosServicos.findIndex(x => x.idProdutoServico === idProdutoServico);
    if (changedPSIndex >= 0) {
      currentProdutosServicos[changedPSIndex].observacao = observacao;
      hookForm.setValue('produtosServicos', [...currentProdutosServicos]);
    }
  }, [
    hookForm,
  ]);

  const columnDefinition = useMemo(() => {
    return GenerateColumnDefinition(
      handleQuantidadeRecebidaChange,
      handleObservacaoChange,
    );
  }, [
    handleQuantidadeRecebidaChange,
    handleObservacaoChange,
  ]);

  useEffect(() => {
    if (open) {
      hookForm.resetField('dataEntrega');
      hookForm.resetField('nroNotaFiscal');
      hookForm.resetField('produtosServicos');
      hookForm.resetField('arquivo');
      hookForm.resetField('limparArquivo');
      hookForm.setValue('registrarEntradaEstoque', false);

      requestAnimationFrame(() => {
        hookForm.setValue('produtosServicos', produtosServicosParaEntrega.map(x => ({
          idProdutoServico: x.idProdutoServico,
          tipo: x.tipo,
          quantidade: x.quantidade ?? 0,
          quantidadeRestante: x.restante ?? 0,
          quantidadeRecebida: x.restante ?? 0,
          observacao: null,
          descricao: x.descricao,
        })));
      });
    }
  }, [open, hookForm, produtosServicosParaEntrega]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      width='70rem'
      title='Nova Entrega Parcial'
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <div className="w-full flex flex-col gap-4 my-4">
        <StyledDataGrid
          tableName="avaliacao_fornecedor_cadastro_para_entrega"
          height="35rem"
          disableRowSelectionOnClick={true}
          disableColumnMenu={false}
          getRowId={(row: ProdutoServicoParaEntrega) => row.idProdutoServico}
          columns={columnDefinition}
          loading={loading || saving}
          rows={produtosServicos ?? []}
          disableColumnSorting={true}
          disableColumnResize={true}
          showToolbar={false}
        />
        <div className="w-full flex flex-row gap-2">
          <DefaultFormInput
            name='nroNotaFiscal'
            label='N° Nota Fiscal'
            reactHookForm={hookForm}
          />

          <DefaultFormDatePickerInput
            name='dataEntrega'
            label='Data da Entrega'
            required={true}
            reactHookForm={hookForm}
          />
        </div>

        <div className="w-full flex flex-row justify-between items-center gap-2">
          <div className="w-[20rem]">
            <DefaultFormDownloadInput
              name='arquivo'
              nameLimparFlag='limparArquivo'
              label='Anexar nota'
              maxFilenameSize={35}
              saving={saving}
              filename={''}
              reactHookForm={hookForm}
              onDeleteFile={() => { }}
            />
          </div>

          <DefaultFormCheckBox
            name='registrarEntradaEstoque'
            label='Registrar Entrada de Produtos no Estoque'
            reactHookForm={hookForm}
          />
        </div>
      </div>
    </BaseFormControlledModal>
  );
}
